import { useEffect, useState } from "react";
import axios from "axios";
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/AuthContext";
import { LoginPage, SignupPage } from "@/AuthPages";
import { AppShell, CapturePage, LibraryPage, MatchPage, GlobalPage, ProfilePage, BillingReturnPage, AdminPage, PublicProfilePage, InboxPage } from "@/AppPages";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ============================================================
   CollabForge — React preview of the WordPress theme.
   The real deliverable is /app/wordpress-theme/collab-forge.zip
   ============================================================ */

const Wave = ({ seed = 13, bars = 32 }) => (
  <div className="cf-wave" data-testid="wave">
    {Array.from({ length: bars }).map((_, i) => (
      <span key={i} style={{ height: `${18 + ((i * seed) % 60)}%` }} />
    ))}
  </div>
);

const Icon = ({ d, stroke = false, size = 24 }) =>
  stroke ? (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{d}</svg>
  ) : (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">{d}</svg>
  );

const FEATURES = [
  { color: "red",   id: "capture", title: "Capture",    body: "One-tap recording at studio-grade 48 kHz. Tag BPM, key, mood, genre while the take is still warm.",      tag: "RIFF · LYRIC · MELODY · RHYTHM", icon: <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2z"/> },
  { color: "amber", id: "library", title: "Library",    body: "Your personal vault. Search across riffs, tags, BPM ranges. Star the gold, archive the rest.",            tag: "SEARCH · FILTER · STAR",         icon: <g><path d="M3 7h18v13H3z" fill="none" stroke="currentColor" strokeWidth="2"/><path d="M3 7l9-4 9 4" fill="none" stroke="currentColor" strokeWidth="2"/></g> },
  { color: "pink",  id: "match",   title: "Riff Match", body: "The “Tinder for songwriters.” Swipe-pair your riff with another artist’s hook. When both swipe right — you’ve got a collab.", tag: "SWIPE · SHUFFLE · PLAYS BOTH",   icon: <path d="M12 21s-7-4.5-9-9a5 5 0 0 1 9-3 5 5 0 0 1 9 3c-2 4.5-9 9-9 9z"/>, accent: true },
  { color: "green", id: "global",  title: "Global",     body: "A live feed of riffs being dropped right now. Filter by genre, mood, region. Find your scene.",            tag: "DISCOVER · TRENDING · NEARBY",   icon: <g fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15 15 0 0 1 0 20"/><path d="M12 2a15 15 0 0 0 0 20"/></g> },
  { color: "blue",  id: "profile", title: "Profile",    body: "Your artist identity. Pinned riffs, instruments, genres, the collaborations you’re open to.",              tag: "@HANDLE · PINNED · OPEN-FOR",    icon: <g fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></g> },
];

const STEPS = [
  { n: "01", t: "Capture in 2 taps",     b: "The mic is one tap away from anywhere in the app. Hum it, strum it, beatbox it." },
  { n: "02", t: "Tag it like you mean it", b: "BPM, key, genre, mood, optional lyric notes. Two seconds now saves two hours later." },
  { n: "03", t: "Match with a stranger", b: "Riff Match pairs your idea with complementary takes from other artists. Swipe to spark." },
  { n: "04", t: "Build a track together",b: "Shared sessions, version history, stems export. Hand the demo to your DAW when it’s ready." },
];

const VOICES = [
  { q: "“Caught a 4 a.m. melody in pajamas. Matched with a producer in São Paulo by Tuesday. We released it in October.”", n: "@Nova_Drums", r: "Drummer · Berlin", i: "N", c: "#ef4444" },
  { q: "“The 'plays both' preview is the killer feature. You stop guessing whether ideas fit — you hear it.”",            n: "@RiffWizard", r: "Guitarist · Manchester", i: "R", c: "#3b82f6" },
  { q: "“My library used to be 600 untitled voice notes. Now it's a searchable instrument. Game over.”",                  n: "@LowEndLena", r: "Bassist · Lagos", i: "L", c: "#10b981" },
];

const PLANS = [
  { e: "FREE",  n: "Demo",   p: "$0",  pe: "/ forever", f: ["Unlimited captures (HIGH quality)", "500 MB library", "10 Riff Matches / week", "Watermark on exports"], c: "Start free",    featured: false },
  { e: "PRO",   n: "Studio", p: "$9",  pe: "/ month",   f: ["PRO audio (48 kHz · 192 kbps)", "Unlimited library + stems export", "Unlimited Riff Matches", "Private collab sessions", "No watermark"], c: "Go Pro", featured: true },
  { e: "LABEL", n: "Forge",  p: "$29", pe: "/ month",   f: ["Everything in Studio", "Team workspaces (up to 8)", "A&R discovery tools", "Splits & rights agreements", "Priority match boost"], c: "Talk to sales", featured: false },
];

const FAQS = [
  { q: "Who owns the recordings I capture?", a: "You do. CollabForge is a tool, not a label. We never license, sell or train on your audio. Splits on collabs are negotiated between you and your match — we provide a simple agreement template." },
  { q: "Does it work offline?",              a: "Capture works offline — record at 30,000 ft, sync when you land. Riff Match and Global need a connection." },
  { q: "What instruments / sources are supported?", a: "Anything that makes sound through your phone or interface. Vocals, guitar, synth, drum machine, kazoo — we have done all five." },
  { q: "How does Riff Match find good pairs?", a: "A blend of musical metadata (key, BPM, tempo curve) and listener signal (which pairs you skip vs heart). It learns fast." },
  { q: "Is there an Android / iOS / Web version?", a: "All three. The web app is what powers the demo above; native iOS and Android ship with the same library." },
];

function Newsletter() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ loading: false, msg: "", ok: null });

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState({ loading: true, msg: "", ok: null });
    try {
      const { data } = await axios.post(`${API}/subscribe`, { email });
      setState({
        loading: false,
        ok: true,
        msg: `You're in. A notification was sent to ${data.notify_to}.`,
      });
      setEmail("");
    } catch (err) {
      const status = err?.response?.status;
      setState({
        loading: false,
        ok: false,
        msg: status === 400 ? "Please enter a valid email." : "Try again?",
      });
    }
  };

  return (
    <>
      <form className="cf-newsletter" onSubmit={submit} data-testid="footer-newsletter">
        <input
          type="email"
          placeholder="you@studio.fm"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          data-testid="footer-newsletter-email"
        />
        <button type="submit" disabled={state.loading} data-testid="footer-newsletter-submit">
          {state.loading ? "Sending…" : state.ok ? "Subscribed" : "Subscribe"}
        </button>
      </form>
      <p
        className={`cf-newsletter__msg ${state.ok === true ? "is-success" : ""} ${state.ok === false ? "is-error" : ""}`}
        aria-live="polite"
        data-testid="footer-newsletter-msg"
      >
        {state.msg}
      </p>
    </>
  );
}

const BrandMark = () => (
  <span className="cf-brand__mark" aria-hidden="true">
    <svg viewBox="0 0 32 32" width="28" height="28" fill="none">
      <rect x="1" y="1" width="30" height="30" rx="8" fill="#ef4444" />
      <path d="M10 22V10l6 4 6-4v12" stroke="#000" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    // No-op: warm the API so the user sees logs.
    axios.get(`${API}/`).catch(() => {});
  }, []);

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      {/* ===== Preview banner ===== */}
      <div className="cf-preview-banner" data-testid="preview-banner">
        <strong>RiffMaster</strong> · Drop riffs, swipe-match with songwriters, get AI compatibility ratings. <Link to="/signup" className="cf-preview-banner__cta">Start free →</Link>
      </div>

      {/* ===== Header ===== */}
      <header className="cf-header" data-testid="site-header">
        <div className="cf-header__inner">
          <a href="#" className="cf-brand" data-testid="brand-link" onClick={closeMenu}>
            <BrandMark />
            <span className="cf-brand__name">RiffMaster</span>
          </a>
          <nav className={`cf-nav ${menuOpen ? "is-open" : ""}`}>
            <ul className="cf-menu">
              <li><a href="#features" onClick={closeMenu}>Features</a></li>
              <li><a href="#how"      onClick={closeMenu}>How it works</a></li>
              <li><a href="#match"    onClick={closeMenu}>Riff Match</a></li>
              <li><a href="#pricing"  onClick={closeMenu}>Pricing</a></li>
              <li><a href="#faq"      onClick={closeMenu}>FAQ</a></li>
            </ul>
          </nav>
          <div className="cf-header__cta">
            <Link to="/login" className="cf-btn cf-btn--ghost" data-testid="header-login-btn">Sign in</Link>
            <Link to="/signup" className="cf-btn cf-btn--primary" data-testid="header-cta-btn">
              <span className="cf-btn__dot" /> Get started
            </Link>
            <button
              type="button"
              className={`cf-burger ${menuOpen ? "is-open" : ""}`}
              aria-label="Toggle navigation menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
            >
              <span /><span /><span />
            </button>
          </div>
        </div>
      </header>

      {/* ===== Hero ===== */}
      <section className="cf-hero" data-testid="hero-section">
        <div className="cf-hero__grain" />
        <div className="cf-container cf-hero__inner">
          <div className="cf-hero__copy">
            <p className="cf-eyebrow">QUICK CAPTURE</p>
            <h1 className="cf-display cf-hero__headline">Drop the riff.</h1>
            <p className="cf-lead">
              A studio in your pocket. Capture riffs, lyrics and melodies the moment they hit —
              then swipe-match with songwriters across the planet.
            </p>
            <div className="cf-hero__ctas">
              <Link to="/signup" className="cf-btn cf-btn--primary cf-btn--lg" data-testid="hero-primary-cta">
                <span className="cf-btn__dot" /> Start free
              </Link>
              <a href="#how" className="cf-btn cf-btn--ghost cf-btn--lg" data-testid="hero-secondary-cta">
                <Icon stroke d={<polygon points="5 3 19 12 5 21 5 3" />} size={14} />
                See how it works
              </a>
            </div>
            <div className="cf-hero__meta">
              <div className="cf-hero__avatars" aria-hidden="true">
                <span style={{ background: "#3b82f6" }}>N</span>
                <span style={{ background: "#ef4444" }}>R</span>
                <span style={{ background: "#10b981" }}>@</span>
                <span style={{ background: "#f59e0b" }}>+</span>
              </div>
              <p>Join songwriters worldwide capturing, matching, and collaborating in real time.</p>
            </div>
          </div>

          <div className="cf-hero__visual" aria-hidden="true">
            <div className="cf-panel">
              <div className="cf-panel__topbar">
                <span className="cf-eyebrow cf-eyebrow--muted">QUICK CAPTURE</span>
                <span className="cf-pill cf-pill--idle"><span /> IDLE</span>
              </div>

              <div className="cf-quality">
                <span className="cf-eyebrow cf-eyebrow--muted">AUDIO INPUT · Browser default</span>
                <div className="cf-quality__tiers">
                  <div className="cf-quality__tier">STANDARD<small>22 kHz · Mono</small></div>
                  <div className="cf-quality__tier is-active">HIGH<small>44.1 kHz · Stereo</small></div>
                  <div className="cf-quality__tier">PRO<small>48 kHz · 192 kbps</small></div>
                </div>
              </div>

              <div className="cf-tabs">
                <button className="cf-tab is-active">RIFF</button>
                <button className="cf-tab">LYRIC</button>
                <button className="cf-tab">MELODY</button>
                <button className="cf-tab">RHYTHM</button>
              </div>

              <div className="cf-recorder">
                <p className="cf-recorder__hint">Tap the button to start recording</p>
                <p className="cf-recorder__time">0:00</p>
                <div className="cf-recorder__controls">
                  <button className="cf-rec-btn" aria-label="Record">
                    <Icon d={<path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2z" />} size={22} />
                  </button>
                  <button className="cf-rec-secondary" aria-label="Upload">
                    <Icon stroke size={18} d={<g><path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></g>} />
                  </button>
                </div>
              </div>

              <div className="cf-meta-row">
                <div className="cf-meta-row__source cf-meta-row__source--human">
                  <Icon size={14} d={<path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5zm0 2c-4 0-9 2-9 6v2h18v-2c0-4-5-6-9-6z" />} />
                  Human-Created
                </div>
                <div className="cf-meta-row__source">
                  <Icon stroke size={14} d={<path d="M12 2l2 5 5 2-5 2-2 5-2-5-5-2 5-2z" />} />
                  AI-Assisted
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="cf-marquee" aria-hidden="true">
          <div className="cf-marquee__track">
            {Array.from({ length: 2 }).map((_, i) =>
              ["rock", "indie", "lofi", "jazz", "trap", "synthwave", "post-punk", "ambient", "folk", "drill", "house", "dnb"].map((g) => (
                <span key={`${i}-${g}`}>● {g}</span>
              ))
            )}
          </div>
        </div>
      </section>

      {/* ===== Features ===== */}
      <section id="features" className="cf-features" data-testid="features-section">
        <div className="cf-container">
          <div className="cf-section-head">
            <p className="cf-eyebrow">THE TOOLKIT</p>
            <h2 className="cf-display cf-display--md">Five modules. One creative loop.</h2>
            <p className="cf-lead cf-lead--muted">Every fleeting idea has a home. Every home has a way back to a collaborator.</p>
          </div>

          <div className="cf-features__grid">
            {FEATURES.map((f) => (
              <article key={f.id} className={`cf-feat ${f.accent ? "cf-feat--accent" : ""}`} data-color={f.color} data-testid={`feature-${f.id}`}>
                <div className="cf-feat__icon"><svg width="24" height="24" viewBox="0 0 24 24" fill={f.icon.props.stroke ? "none" : "currentColor"}>{f.icon}</svg></div>
                <h3>{f.title}</h3>
                <p>{f.body}</p>
                <span className="cf-feat__tag">{f.tag}</span>
              </article>
            ))}
            <article className="cf-feat cf-feat--cta" data-testid="feature-cta-card">
              <h3>Ready to drop?</h3>
              <p>Start free. Upgrade when your library starts paying rent.</p>
              <Link to="/signup" className="cf-btn cf-btn--primary" data-testid="features-cta-btn">Start free</Link>
            </article>
          </div>
        </div>
      </section>

      {/* ===== How it works ===== */}
      <section id="how" className="cf-how" data-testid="how-section">
        <div className="cf-container">
          <div className="cf-section-head cf-section-head--left">
            <p className="cf-eyebrow">THE LOOP</p>
            <h2 className="cf-display cf-display--md">From shower-thought<br />to signed track.</h2>
          </div>
          <ol className="cf-steps">
            {STEPS.map((s) => (
              <li key={s.n} className="cf-step">
                <span className="cf-step__num">{s.n}</span>
                <h3>{s.t}</h3>
                <p>{s.b}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ===== Riff Match preview ===== */}
      <section id="match" className="cf-match-preview" data-testid="match-section">
        <div className="cf-container cf-match-preview__inner">
          <div className="cf-section-head cf-section-head--left">
            <p className="cf-eyebrow">RIFF MATCH</p>
            <h2 className="cf-display cf-display--md">Pair the magic.</h2>
            <p className="cf-lead cf-lead--muted">
              Two riffs. One question: <em>do they play both?</em> Swipe up to swap order. Tap heart to send a collab request.
            </p>
            <ul className="cf-bullets">
              <li>↑↓ Swap which riff leads</li>
              <li>✕ Skip — never see this pair again</li>
              <li>♥ Match — start a session</li>
              <li>▶ Plays-both preview before you decide</li>
            </ul>
          </div>

          <div className="cf-match-card" aria-hidden="true">
            <div className="cf-match-card__row">
              <div className="cf-match-card__head">
                <span className="cf-pill cf-pill--human">● HUMAN</span>
                <span className="cf-pill cf-pill--plays">▶ PLAYS BOTH</span>
                <span className="cf-handle">@Nova_Drums</span>
              </div>
              <h4>Midnight Groove</h4>
              <Wave seed={13} />
              <p className="cf-meta">92 BPM · Em · rock · moody · riff</p>
            </div>
            <div className="cf-match-card__swap">
              <Icon stroke size={18} d={<g stroke="#ef4444"><path d="M7 7l5-5 5 5M12 2v20M17 17l-5 5-5-5" /></g>} />
            </div>
            <div className="cf-match-card__row">
              <div className="cf-match-card__head">
                <span className="cf-pill cf-pill--human">● HUMAN</span>
                <span className="cf-handle">@RiffWizard</span>
              </div>
              <h4>Ghost Strings</h4>
              <Wave seed={17} />
              <p className="cf-meta">85 BPM · Dm · indie · melancholic · riff</p>
            </div>
            <div className="cf-match-card__actions">
              <button className="cf-circle cf-circle--skip" aria-label="Skip">
                <Icon stroke size={20} d={<path d="M18 6 6 18M6 6l12 12" strokeWidth="2.5" />} />
              </button>
              <button className="cf-circle cf-circle--like" aria-label="Match">
                <Icon size={22} d={<path d="M12 21s-7-4.5-9-9a5 5 0 0 1 9-3 5 5 0 0 1 9 3c-2 4.5-9 9-9 9z" />} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Voices ===== */}
      <section className="cf-voices" data-testid="voices-section">
        <div className="cf-container">
          <div className="cf-section-head">
            <p className="cf-eyebrow">FROM THE COMMUNITY</p>
            <h2 className="cf-display cf-display--md">Voices in the booth.</h2>
          </div>
          <div className="cf-voices__grid">
            {VOICES.map((v, i) => (
              <figure key={i} className="cf-voice" data-testid={`voice-${i + 1}`}>
                <blockquote>{v.q}</blockquote>
                <figcaption>
                  <span className="cf-avatar" style={{ background: v.c }}>{v.i}</span>
                  <div>
                    <strong>{v.n}</strong>
                    <small>{v.r}</small>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Pricing ===== */}
      <section id="pricing" className="cf-pricing" data-testid="pricing-section">
        <div className="cf-container">
          <div className="cf-section-head">
            <p className="cf-eyebrow">PLANS</p>
            <h2 className="cf-display cf-display--md">Free to start. Cheap to ship.</h2>
          </div>
          <div className="cf-pricing__grid">
            {PLANS.map((p, i) => (
              <article key={p.n} className={`cf-plan ${p.featured ? "cf-plan--featured" : ""}`} data-testid={`plan-${i + 1}`}>
                {p.featured && <span className="cf-badge">MOST POPULAR</span>}
                <header>
                  <p className="cf-eyebrow">{p.e}</p>
                  <h3>{p.n}</h3>
                  <p className="cf-plan__price">{p.p} <small>{p.pe}</small></p>
                </header>
                <ul>
                  {p.f.map((feat) => <li key={feat}>{feat}</li>)}
                </ul>
                <Link to="/signup" className={`cf-btn ${p.featured ? "cf-btn--primary" : "cf-btn--ghost"} cf-btn--block`} data-testid={`plan-${i + 1}-cta`}>
                  {p.c}
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section id="faq" className="cf-faq" data-testid="faq-section">
        <div className="cf-container cf-container--narrow">
          <div className="cf-section-head cf-section-head--left">
            <p className="cf-eyebrow">FAQ</p>
            <h2 className="cf-display cf-display--md">Things people ask before they tap record.</h2>
          </div>
          <div className="cf-faq__list">
            {FAQS.map((f, i) => (
              <details key={i} className="cf-faq__item" open={i === 0} data-testid={`faq-item-${i + 1}`}>
                <summary>
                  <span>{f.q}</span>
                  <span className="cf-faq__icon" aria-hidden="true">+</span>
                </summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Final CTA ===== */}
      <section className="cf-final" data-testid="final-cta-section">
        <div className="cf-container cf-final__inner">
          <p className="cf-eyebrow">YOUR TURN</p>
          <h2 className="cf-display cf-display--xl">Start dropping riffs.</h2>
          <p className="cf-lead">The next great hook is already in your head. Let’s get it out.</p>
          <div className="cf-final__ctas">
            <Link to="/signup" className="cf-btn cf-btn--primary cf-btn--lg" data-testid="final-primary-cta">
              <span className="cf-btn__dot" /> Get started — free
            </Link>
            <a href="#features" className="cf-btn cf-btn--ghost cf-btn--lg" data-testid="final-secondary-cta">Browse features</a>
          </div>
        </div>
      </section>

      {/* ===== Footer ===== */}
      <footer className="cf-footer" data-testid="site-footer">
        <div className="cf-footer__inner">
          <div className="cf-footer__brand">
            <a href="#" className="cf-brand">
              <BrandMark />
              <span className="cf-brand__name">CollabForge</span>
            </a>
            <p className="cf-footer__tag">A studio in your pocket. Built for the in-between moments where the best ideas live.</p>
          </div>
          <div className="cf-footer__cols">
            <div className="cf-footer__col"><h4>Product</h4><ul><li><a href="#features">Capture</a></li><li><a href="#features">Library</a></li><li><a href="#match">Riff Match</a></li><li><a href="#features">Global feed</a></li></ul></div>
            <div className="cf-footer__col"><h4>Company</h4><ul><li><a href="#">About</a></li><li><a href="#">Press kit</a></li><li><a href="#">Careers</a></li><li><a href="#">Contact</a></li></ul></div>
            <div className="cf-footer__col"><h4>Resources</h4><ul><li><a href="#faq">FAQ</a></li><li><a href="#">Changelog</a></li><li><a href="#">Status</a></li><li><a href="#">Privacy</a></li></ul></div>
            <div className="cf-footer__col cf-footer__col--newsletter">
              <h4>Stay tuned</h4>
              <p>Tour the next build before anyone else.</p>
              <Newsletter />
            </div>
          </div>
        </div>
        <div className="cf-footer__bottom">
          <span>© {new Date().getFullYear()} CollabForge. All rights reserved.</span>
          <span className="cf-footer__legal">
            <a href="#">Terms</a><a href="#">Privacy</a><a href="#">Cookies</a>
          </span>
        </div>
      </footer>
    </>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div style={{minHeight:"50vh",display:"flex",alignItems:"center",justifyContent:"center",color:"#8a8a90"}}>Loading…</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/billing/return" element={<ProtectedRoute><BillingReturnPage /></ProtectedRoute>} />
          <Route path="/app" element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
            <Route index element={<Navigate to="capture" replace />} />
            <Route path="capture" element={<CapturePage />} />
            <Route path="library" element={<LibraryPage />} />
            <Route path="match" element={<MatchPage />} />
            <Route path="global" element={<GlobalPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="inbox" element={<InboxPage />} />
            <Route path="admin" element={<AdminPage />} />
            <Route path="u/:userId" element={<PublicProfilePage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
